import axios from 'axios';
import { API_BASE_URL } from './config';
import { PHOTO_PREFIX, isInlinePhoto, isPhotoRef } from './photoValue';

// A submission photo is stored as a lightweight "photo:<key>" reference that
// points at an object in storage, instead of embedding the image bytes in the
// submission JSON. Legacy submissions may still hold an inline data URL; both
// shapes are handled here so old and new records render the same way.
export { hasPhotoValue, isInlinePhoto, isPhotoRef } from './photoValue';

// Cache resolved data URLs by value so repeated renders/exports don't re-fetch,
// and a just-uploaded photo renders instantly without a round-trip.
const dataUrlCache = new Map();

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Failed to read photo'));
    reader.readAsDataURL(blob);
  });

// Upload a compressed image data URL to object storage; returns a durable
// "photo:<key>" reference to store on the submission in place of the bytes.
export const uploadPhoto = async (dataUrl) => {
  const res = await axios.post(`${API_BASE_URL}/api/photos`, { data: dataUrl });
  const ref = `${PHOTO_PREFIX}${res.data.key}`;
  dataUrlCache.set(ref, dataUrl);
  return ref;
};

// Resolve any stored photo value (legacy inline data URL or "photo:<key>" ref)
// to a displayable/embeddable data URL, or null if there is no photo.
export const resolvePhotoDataUrl = async (value) => {
  if (isInlinePhoto(value)) return value;
  if (!isPhotoRef(value)) return null;
  if (dataUrlCache.has(value)) return dataUrlCache.get(value);
  const key = value.slice(PHOTO_PREFIX.length);
  const res = await axios.get(`${API_BASE_URL}/api/photos/${key}`, { responseType: 'blob' });
  const dataUrl = await blobToDataUrl(res.data);
  dataUrlCache.set(value, dataUrl);
  return dataUrl;
};
